import type { ComponentChildren, JSX } from "preact";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { MoreVertical } from "./icons.tsx";

/** How close to the window's edge the card may come before it is moved. */
const EDGE = 8;

/**
 * Which way the card opens. It hangs below the trigger and lines up with the
 * trigger's right edge, until that would put it off the screen: the last row of
 * a list has no room underneath, and a trigger near the left edge has none to
 * its left. Nothing here can set an inline `style` — the site's content
 * security policy forbids them — so each side is a class instead.
 */
interface Placement {
  up: boolean;
  left: boolean;
}

export interface MenuItem {
  label: string;
  onSelect: () => void;
  /** Destructive items are drawn in the bad colour. */
  destructive?: boolean;
}

interface Props {
  /** Names the row this menu belongs to, since the trigger is just a `⋮`. */
  label: string;
  items: MenuItem[];
  /** Replaces the `⋮` when the trigger has something of its own. */
  trigger?: ComponentChildren;
  /** A line of context above the items, such as who is signed in. */
  header?: ComponentChildren;
}

/**
 * A plain absolutely positioned card of buttons. It closes on an outside
 * click, on Escape and on choosing an item, and hands focus back to the
 * trigger, which is everything a popover library would have been carried for.
 */
export function OverflowMenu({ label, items, trigger: face, header }: Props) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState<Placement>({ up: false, left: false });
  const wrap = useRef<HTMLDivElement | null>(null);
  const trigger = useRef<HTMLButtonElement | null>(null);
  const card = useRef<HTMLDivElement | null>(null);

  // Measured after the card is in the document but before the browser paints,
  // so a card that has to move does not appear in the wrong place first. It is
  // measured again on a resize: a placement chosen for one window size can put
  // the card off the edge of a smaller one.
  useLayoutEffect(() => {
    if (!open) return;
    function measure(): void {
      const anchor = trigger.current?.getBoundingClientRect();
      const box = card.current?.getBoundingClientRect();
      if (!anchor || !box) return;
      setPlace({
        up:
          anchor.bottom + box.height + EDGE > window.innerHeight &&
          anchor.top > box.height,
        left: anchor.right - box.width < EDGE,
      });
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onPointer(event: MouseEvent): void {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, [open]);

  function close(): void {
    setOpen(false);
    trigger.current?.focus();
  }

  function move(from: EventTarget | null, step: number): void {
    const buttons = [
      ...(wrap.current?.querySelectorAll<HTMLElement>("[role=menuitem]") ?? []),
    ];
    if (buttons.length === 0) return;
    const index = buttons.indexOf(from as HTMLElement);
    // The trigger is not in the list, so Down from it lands on the first item
    // and Up on the last, which is what a menu does.
    const next =
      index < 0
        ? step > 0
          ? buttons[0]
          : buttons[buttons.length - 1]
        : buttons[(index + step + buttons.length) % buttons.length];
    next?.focus();
  }

  /**
   * On the trigger as well as the card: the trigger still holds focus when the
   * menu opens, and the card is its sibling, so its keys never reach the card.
   */
  function onKeys(event: JSX.TargetedKeyboardEvent<HTMLElement>): void {
    if (!open) return;
    if (event.key === "Escape") close();
    else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      move(event.target, event.key === "ArrowDown" ? 1 : -1);
    }
  }

  return (
    <div class="menu-wrap" ref={wrap}>
      <button
        type="button"
        class={face ? "account-btn" : "icon-btn"}
        ref={trigger}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        onKeyDown={onKeys}
      >
        {face ?? <MoreVertical />}
      </button>
      {open && (
        <div
          class={[
            "menu-card",
            face && "menu-card-narrow",
            place.up && "menu-card-up",
            place.left && "menu-card-left",
          ]
            .filter(Boolean)
            .join(" ")}
          ref={card}
          role="menu"
          onKeyDown={onKeys}
        >
          {header && <p class="menu-header">{header}</p>}
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              class={item.destructive ? "menu-item menu-item-bad" : "menu-item"}
              onClick={() => {
                close();
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
